import { useParams, Link, Navigate } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Clock, User, Calendar } from "lucide-react";
import Footer from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { ArticleSchema, BreadcrumbSchema } from "@/components/StructuredData";
import { useEffect, useMemo, useState } from "react";
import { trackBlogRead } from "@/lib/analytics";
import DOMPurify from 'dompurify';
import { useQuery } from "@tanstack/react-query";
import { blogPostQuery, LOAD_ERROR_MESSAGE } from "@/lib/publicQueries";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import imgPhotoPrep from "@/assets/blog/photoshoot-preparation.jpg";
import imgVideoMarketing from "@/assets/blog/video-marketing.jpg";
import imgRealEstate from "@/assets/blog/real-estate-photography.jpg";
import imgImagefilmCosts from "@/assets/blog/imagefilm-costs.jpg";
import imgMannheimLocations from "@/assets/blog/mannheim-locations.jpg";

// Specific fallbacks for posts whose remote images are missing
const FALLBACK_BY_SLUG: Record<string, string> = {
  "instagram-reels-strategie-unternehmen": imgVideoMarketing,
  "professionelle-fotografie-mannheim-tipps": imgPhotoPrep,
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [readingProgress, setReadingProgress] = useState(0);
  
  const { data: post, isLoading, isError } = useQuery(blogPostQuery(slug ?? ""));

  // Map known invalid asset paths and legacy public paths to imported assets
const IMAGE_MAP: Record<string, string> = {
  "/blog/photoshoot-preparation.jpg": imgPhotoPrep,
  "/blog/video-marketing.jpg": imgVideoMarketing,
  "/blog/real-estate-photography.jpg": imgRealEstate,
  "/blog/imagefilm-costs.jpg": imgImagefilmCosts,
  "/blog/mannheim-locations.jpg": imgMannheimLocations,
  "/src/assets/locations/mannheim-hero.jpg": imgMannheimLocations,
  "/src/assets/services/videography-professional.jpg": imgImagefilmCosts,
  // Broken remote URLs mapped to local fallbacks
  "https://buhajnrjzecedelnxllv.supabase.co/storage/v1/object/public/project-media/gallery/1760101610766-hep3jl2k8u.jpg": imgVideoMarketing,
  "https://buhajnrjzecedelnxllv.supabase.co/storage/v1/object/public/project-media/gallery/1760101649329-hep3jl5tpq.jpg": imgPhotoPrep,
};

  const mapImageUrl = (url?: string) => {
    if (!url) return undefined;
    return IMAGE_MAP[url] || url;
  };

  // SECURITY: Sanitize HTML content to prevent XSS attacks and fix broken image src.
  // DOMPurify needs the browser DOM, so the body is sanitized after mount
  // (server HTML still contains the title, meta and h1).
  const [sanitizedContent, setSanitizedContent] = useState('');
  useEffect(() => {
    if (!post || typeof DOMPurify.sanitize !== 'function') { setSanitizedContent(''); return; }
    const fixed = (post.content || '')
      .split('/src/assets/locations/mannheim-hero.jpg').join(imgMannheimLocations)
      .split('/src/assets/services/videography-professional.jpg').join(imgImagefilmCosts)
      .split('/blog/photoshoot-preparation.jpg').join(imgPhotoPrep)
      .split('/blog/video-marketing.jpg').join(imgVideoMarketing)
      .split('/blog/real-estate-photography.jpg').join(imgRealEstate)
      .split('/blog/imagefilm-costs.jpg').join(imgImagefilmCosts)
      .split('/blog/mannheim-locations.jpg').join(imgMannheimLocations);
    const clean = DOMPurify.sanitize(fixed, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'code', 'pre'],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class'],
      ALLOW_DATA_ATTR: false,
    });
    // Only one h1 per page: headings inside the article body start at h2.
    setSanitizedContent(clean.replace(/<(\/?)h1(\s|>)/gi, '<$1h2$2'));
  }, [post]);

  useEffect(() => {
    if (post) {
      trackBlogRead(post.slug, post.title);
    }
  }, [post]);

  // Reading progress tracker
  useEffect(() => {
    const updateReadingProgress = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrolled = window.scrollY;
      const progress = (scrolled / documentHeight) * 100;
      setReadingProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', updateReadingProgress);
    return () => window.removeEventListener('scroll', updateReadingProgress);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="section-padding border-b border-border">
          <div className="container-custom">
            <Skeleton className="h-10 w-40 mb-8" />
          </div>
        </header>
        <article className="section-padding">
          <div className="container-custom max-w-3xl">
            <Skeleton className="h-8 w-32 mb-6" />
            <Skeleton className="h-16 w-full mb-6" />
            <Skeleton className="h-6 w-3/4 mb-8" />
            <Skeleton className="aspect-[16/9] w-full mb-12" />
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        </article>
      </div>
    );
  }

  if (!post && isError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <p role="alert" className="text-center text-muted-foreground py-12">{LOAD_ERROR_MESSAGE}</p>
      </div>
    );
  }

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const ogImage = post.featured_image?.startsWith('http')
    ? post.featured_image
    : post.featured_image
      ? `https://clicksdmp.com${post.featured_image.startsWith('/') ? '' : '/'}${post.featured_image}`
      : undefined;

  return (
    <>
      <SEOHead
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt}
        canonical={`https://clicksdmp.com/blog/${post.slug}`}
        {...(ogImage ? { ogImage } : {})}
        ogType="article"
        schema={
          <>
            <BreadcrumbSchema
              items={[
                { name: "Startseite", url: "https://clicksdmp.com/" },
                { name: "Blog", url: "https://clicksdmp.com/blog" },
                { name: post.title, url: `https://clicksdmp.com/blog/${post.slug}` },
              ]}
            />
            <ArticleSchema
              article={{
                title: post.title,
                description: post.excerpt,
                url: `https://clicksdmp.com/blog/${post.slug}`,
                datePublished: post.published_at || post.created_at,
                dateModified: post.updated_at || post.published_at || post.created_at,
                ...(ogImage ? { image: ogImage } : {}),
              }}
            />
          </>
        }
      />

      {/* Reading Progress Bar */}
      <div className="blog-progress">
        <div 
          className="blog-progress-bar" 
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="section-padding border-b border-border">
          <div className="container-custom">
            <Link to="/">
              <Button variant="ghost" size="sm" className="gap-2 mb-8">
                <ArrowLeft className="w-4 h-4" />
                Zurück zur Startseite
              </Button>
            </Link>
          </div>
        </header>

        {/* Article */}
        <article className="section-padding">
          <div className="container-custom max-w-4xl">
            {/* Meta */}
            <div className="mb-6 flex items-center justify-center">
              <span className="px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold tracking-wide uppercase border border-primary/20">
                {post.category}
              </span>
            </div>

            {/* Title */}
            <h1 className="font-serif text-5xl md:text-7xl font-bold mb-8 leading-tight text-center bg-gradient-to-r from-foreground via-foreground to-foreground/80 bg-clip-text">
              {post.title}
            </h1>

            {/* Author & Date */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground mb-12 pb-8 border-b border-border/50">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                <span className="font-medium">Clicks DMP Team</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                <span>
                  {format(
                    new Date(post.published_at || post.created_at),
                    "dd. MMMM yyyy",
                    { locale: de }
                  )}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-primary" />
                <span>{Math.ceil(sanitizedContent.split(' ').length / 200)} Min. Lesezeit</span>
              </div>
            </div>

            {/* Featured Image */}
            {post.featured_image && (
              <div className="aspect-[21/9] rounded-2xl overflow-hidden mb-16 shadow-2xl">
                <img
                  src={mapImageUrl(post.featured_image)}
                  alt={post.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  loading="eager"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).src = FALLBACK_BY_SLUG[post.slug] || '/placeholder.svg'; }}
                />
              </div>
            )}

            {/* Content - Enhanced Typography */}
            <div 
              className="blog-content prose-none"
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />

            {/* Keywords */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-16 pt-10 border-t-2 border-primary/20">
                <p className="text-sm font-semibold text-muted-foreground mb-4 uppercase tracking-wider">Themen & Keywords:</p>
                <div className="flex flex-wrap gap-3">
                  {post.tags.map((tag: string) => (
                    <span
                      key={tag}
                      className="px-4 py-2 rounded-full bg-primary/5 hover:bg-primary/10 text-sm font-medium border border-primary/20 transition-colors cursor-default"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="mt-16 p-10 rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-background border-2 border-primary/20 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-50" />
              <div className="relative z-10">
                <h2 className="font-serif text-3xl font-bold mb-4">
                  Bereit für Ihr Projekt?
                </h2>
                <p className="text-muted-foreground mb-8 text-lg">
                  Kontaktieren Sie uns für ein kostenloses Erstgespräch
                </p>
                <Link to="/">
                  <Button size="lg" className="font-semibold text-lg px-8 py-6">
                    Jetzt Beratung buchen
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* CTA to Blog */}
        <section className="section-padding bg-muted/30">
          <div className="container-custom max-w-5xl text-center">
            <h2 className="text-3xl font-bold mb-4">Mehr Insights entdecken</h2>
            <p className="text-muted-foreground mb-8">
              Entdecken Sie weitere Artikel rund um Video, Fotografie und Marketing
            </p>
            <Link to="/blog">
              <Button size="lg" variant="outline">
                Alle Artikel ansehen
              </Button>
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default BlogPost;
